import React, { useState } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { Form, Col } from "react-bootstrap";

const TextInput = ({
  name,
  label,
  control,
  errors,
  type = "text",
  required = false,
  rules = {},
  disabled = false,
  placeholder = "",
  min,
  className,
  max,
  colSize,
  multiline = false,
  inputMode,
  pattern,
  maxLength,
  rows = 3,
  onChange,
  value, // Added to accept external value prop
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleNumberInput = (value, field) => {
    if (value === "") {
      field.onChange("");
      onChange?.("");
      return;
    }

    // Allow only digits 0-9 and a single decimal point
    const sanitizedValue = value.replace(/[^0-9.]/g, "");
    const decimalCount = sanitizedValue.split(".").length - 1;
    if (decimalCount > 1) return;

    if (sanitizedValue !== "") {
      field.onChange(sanitizedValue);
      onChange?.(sanitizedValue);
    }
  };

  const handleNumberBlur = (value, field) => {
    if (value === "") return;

    let numericValue = parseFloat(value);
    if (isNaN(numericValue)) return;

    if (typeof min !== "undefined" && numericValue < min) {
      numericValue = min;
    }
    if (typeof max !== "undefined" && numericValue > max) {
      numericValue = max;
    }

    field.onChange(numericValue.toString());
    onChange?.(numericValue.toString());
  };

  // Prevent single quote (') and double quote (")
  const sanitizeText = (value) => value.replace(/["]/g, "");

  const EyeIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {showPassword ? (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );

  return (
    <Col md={colSize} className={className}>
      <Form.Group controlId={name}>
        {label && (
          <Form.Label>
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </Form.Label>
        )}

        <div className="position-relative">
          <Controller
            name={name}
            control={control}
            rules={{ required, ...rules }}
            render={({ field }) => {
              if (type === "file") {
                return (
                  <div className="custom-file-input border rounded">
                    <div className="d-flex justify-content-between align-items-center py-0.5 input-file">
                      <Form.Label
                        htmlFor={name}
                        className="fs-11 px-2 mb-0 cursor-pointer text-elipisis"
                        style={{ flex: 1, cursor: "pointer" }}
                      >
                        {value ||
                          field.value?.title ||
                          field.value?.originalName ||
                          "Attach Document"}
                      </Form.Label>
                      <div className="d-flex align-items-center">
                        <Form.Label
                          htmlFor={name}
                          className="btn btn-success btn-sm mb-0 fs-10"
                          style={{
                            cursor: "pointer",
                            borderTopRightRadius: "4px",
                            borderBottomRightRadius: "4px",
                            borderTopLeftRadius: "0",
                            borderBottomLeftRadius: "0",
                          }}
                        >
                          Browse
                        </Form.Label>
                        <Form.Control
                          type="file"
                          id={name}
                          className="d-none"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            field.onChange(file);
                            onChange?.(file);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              return multiline ? (
                <Form.Control
                  as="textarea"
                  {...field}
                  rows={rows}
                  min={min}
                  max={max}
                  autoComplete="off"
                  disabled={disabled}
                  className={className}
                  onChange={(e) => {
                    let value = sanitizeText(e.target.value);
                    if (value.trim() === "" && value !== "") return;
                    field.onChange(value);
                    onChange?.(value);
                  }}
                  placeholder={placeholder}
                  isInvalid={!!errors?.[name]}
                  {...props}
                />
              ) : (
                <Form.Control
                  {...field}
                  type={
                    type === "password"
                      ? showPassword
                        ? "text"
                        : "password"
                      : type === "number"
                      ? "text"
                      : type
                  }
                  min={min}
                  max={max}
                  disabled={disabled}
                  className={className}
                  step={type === "number" ? "any" : undefined}
                  inputMode={type === "number" ? "decimal" : undefined}
                  placeholder={placeholder}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (type === "number") {
                      handleNumberInput(value, field);
                    } else {
                      value = sanitizeText(value);
                      if (value.trim() === "" && value !== "") return;
                      field.onChange(value);
                      onChange?.(value);
                    }
                  }}
                  onBlur={(e) => {
                    if (type === "number") {
                      handleNumberBlur(e.target.value, field);
                    }
                  }}
                  isInvalid={!!errors?.[name]}
                  style={type === "password" ? { paddingRight: "35px" } : {}}
                  {...props}
                />
              );
            }}
          />

          {type === "password" && !multiline && (
            <button
              type="button"
              className="position-absolute end-2 top-50 translate-middle-y bg-transparent border-0 p-0"
              style={{
                right: "10px",
                transform: "translateY(-50%)",
                color: "gray",
              }}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon />
            </button>
          )}
        </div>

        {errors?.[name] && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {errors[name].message}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    </Col>
  );
};

TextInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  type: PropTypes.string,
  required: PropTypes.bool,
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  colSize: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      span: PropTypes.number,
      offset: PropTypes.number,
    }),
  ]),
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  onChange: PropTypes.func,
  value: PropTypes.any, // Added for external value prop
};

TextInput.defaultProps = {
  type: "text",
  required: false,
  disabled: false,
  placeholder: "",
  multiline: false,
  rows: 3,
  onChange: undefined,
  value: undefined,
};

export default TextInput;
